import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { useRef, useState } from 'react';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { type BrokerAlert, updateBrokerFeeBandState } from '@/services/api/compliance-alerts';

const REJECT_REASONS = [
  { label: 'Fees too high', value: 'FEES_TOO_HIGH' },
  { label: 'Other', value: 'OTHER' },
] as const;

type RejectReason = (typeof REJECT_REASONS)[number]['value'];

interface BrokerFeeBandReviewActionsProps {
  alert: BrokerAlert;
  onClose: () => void;
  /** Dismisses the alert with the same justification (second, non-atomic call) */
  onDismiss: (justification: string) => Promise<void>;
}

export const BrokerFeeBandReviewActions = ({ alert, onClose, onDismiss }: BrokerFeeBandReviewActionsProps) => {
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState<RejectReason | null>(null);
  // The outcome call and the dismiss are two non-atomic requests. If the dismiss
  // fails and the operator retries in the confirm dialog, the (already successful)
  // state-changing outcome call must not fire a second time.
  const isOutcomeApplied = useRef(false);

  // The alert payload is free-form Dynamo data — guard the fields this flow depends on
  // before letting them reach a state-changing broker mutation.
  const isAlertValid = typeof alert.broker_name === 'string' && alert.broker_name.length > 0 && typeof alert.fee_band_id === 'string' && alert.fee_band_id.length > 0;

  const applyOutcome = async (state: 'ACTIVE' | 'REJECTED', justification: string, rejection_reason?: RejectReason) => {
    if (!isOutcomeApplied.current) {
      await updateBrokerFeeBandState({
        broker_account_id: alert.pid,
        fee_band_id: alert.fee_band_id,
        state,
        ...(rejection_reason ? { rejection_reason } : {}),
        comment: justification,
      });
      isOutcomeApplied.current = true;
    }
    await onDismiss(justification);
  };

  const handleApprove = () => {
    isOutcomeApplied.current = false;
    return confirmAction({
      title: 'Approve Fee Band',
      description: `Proceeding with this action will approve the broker fee band for ${alert.broker_name} and dismiss the alert with ID ${alert.alert_id}. Your action here will be logged and will be auditable.`,
      actionLabel: 'Approve Fee Band',
      onConfirm: (justification) => applyOutcome('ACTIVE', justification),
    });
  };

  const handleRejectContinue = async () => {
    if (!rejectReason) return;
    setIsRejectDialogOpen(false);
    isOutcomeApplied.current = false;
    await confirmAction({
      title: 'Reject Fee Band',
      description: `Proceeding with this action will reject the broker fee band for ${alert.broker_name} and dismiss the alert with ID ${alert.alert_id}. Your action here will be logged and will be auditable.`,
      actionLabel: 'Reject Fee Band',
      destructive: true,
      onConfirm: (justification) => applyOutcome('REJECTED', justification, rejectReason),
    });
  };

  return (
    <div className="flex flex-col items-end gap-2">
      {isAlertValid ? null : <p className="text-sm text-red-500">This alert is missing its broker name or fee band id — review actions are disabled.</p>}
      <div className="flex justify-end gap-2">
        <Button onClick={onClose} type="button" variant="outline">
          Close
        </Button>
        <Button disabled={!isAlertValid} onClick={handleApprove} type="button" variant="primary">
          Approve Fee Band
        </Button>
        <Button disabled={!isAlertValid} onClick={() => setIsRejectDialogOpen(true)} type="button" variant="destructive">
          Reject Fee Band
        </Button>
      </div>

      <Dialog onOpenChange={setIsRejectDialogOpen} open={isRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Fee Band</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label>Reject reason</Label>
            <Select onValueChange={(value) => setRejectReason(value as RejectReason)} value={rejectReason ?? undefined}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {REJECT_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsRejectDialogOpen(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={!rejectReason} onClick={handleRejectContinue} type="button" variant="destructive">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
BrokerFeeBandReviewActions.displayName = 'BrokerFeeBandReviewActions';
